import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Ruler } from 'lucide-react';
import type { SizeGuideToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useSizeGuidesList, useSizeGuideMutations } from '~/hooks/use-size-guides';

const Bool = ({ on }: { on: boolean }) =>
	on ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>;

/** Size guides (Μεγεθολόγιο) grid — parity with the legacy admin grid (Title/Code/Visible); editing opens the full editor. */
export function SizeGuidesGridView() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { data, isLoading, isError, refetch } = useSizeGuidesList();
	const { remove } = useSizeGuideMutations();

	const [deleteRow, setDeleteRow] = useState<SizeGuideToken | null>(null);

	const columns = useMemo<SystemGridColumn<SizeGuideToken>[]>(
		() => [
			{
				key: 'title',
				header: t('attributes.sizeGuides.columns.title'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.tl ?? '',
				exportValue: o => o.tl ?? '',
				render: o => (
					<span className="truncate text-sm font-medium text-foreground" title={o.tl ?? ''}>
						{o.tl || '—'}
					</span>
				)
			},
			{
				key: 'code',
				header: t('attributes.sizeGuides.columns.code'),
				width: '220px',
				sortValue: o => o.cd ?? '',
				exportValue: o => o.cd ?? '',
				render: o => <span className="truncate font-mono text-xs text-foreground-muted">{o.cd || '—'}</span>
			},
			{
				key: 'visible',
				header: t('attributes.sizeGuides.columns.visible'),
				width: '90px',
				align: 'center',
				sortValue: o => (o.iv ? 1 : 0),
				exportValue: o => (o.iv ? t('common.yes') : t('common.no')),
				render: o => <Bool on={Boolean(o.iv)} />
			}
		],
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<SizeGuideToken>
				gridId="size-guides"
				title={t('attributes.sizeGuides.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={o => o.id ?? o.tl}
				columns={columns}
				searchPredicate={(o, q) => (o.tl ?? '').toLowerCase().includes(q) || (o.cd ?? '').toLowerCase().includes(q)}
				defaultSort={{ key: 'title', dir: 'asc' }}
				emptyIcon={<Ruler size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('attributes.sizeGuides.add')}
				onAdd={() => navigate('/attributes/size-guides/create')}
				onEdit={o => navigate(`/attributes/size-guides/${o.id}`)}
				onDelete={setDeleteRow}
				exportFilename="size-guides"
			/>

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('attributes.sizeGuides.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('attributes.sizeGuides.deleteBody', { name: deleteRow?.tl || '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
